import { useState } from 'react';
import { paths } from '@/routes/paths';
import { useTranslation } from 'react-i18next';
import { useSelfServeAccess } from '@/hooks/useSelfServeAccess';
import { UpgradeModal } from '@/components/billing/UpgradeModal';
import { useUserPermissions } from '@/hooks/use-user-permissions';
import { FEATURE_FLAGS, useFeatureFlag } from '@/hooks/useFeatureFlag';
import { ComposerDialog } from '@/sections/white-glove/calendar/composer-dialog';
import { useBillingControllerGetQuota } from '@/apis/backend/generated/billing/billing';
import { Outlet, useNavigate, useLocation, createFileRoute } from '@tanstack/react-router';
import {
  hasWhiteGloveWork,
  useWhiteGloveAccess,
} from '@/sections/white-glove/use-white-glove-access';

import { Box, Tab, Tabs, Button, Container, Typography } from '@mui/material';
import {
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  Architecture as ArchitectureIcon,
} from '@mui/icons-material';

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/c/$clientId/templates')({
  component: TemplatesLayout,
});

function activeTabFromPath(pathname: string): 'library' | 'my' {
  if (pathname.endsWith('/templates/my')) return 'my';
  return 'library';
}

function TemplatesLayout() {
  const { t } = useTranslation('templates');
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId, clientId } = Route.useParams();
  const tp = paths.dashboard.tenant(orgId, clientId);
  const base = paths.dashboard.tenantRoot(orgId, clientId);

  // The email builder is a canvas surface, so its entry point follows the same
  // gate as the rest of the canvas features: platform admins always, plus orgs
  // opted into the canvas_creative_modes flag.
  const { isPlatformAdmin } = useUserPermissions();
  const canvasModesEnabled = useFeatureFlag(FEATURE_FLAGS.CANVAS_CREATIVE_MODES);
  // Both header buttons and the Library tab are DIY doorways: they mint a new
  // email, which only a brand on the plan that buys the builder can do. "My"
  // stays, so a managed brand still has its own emails and its Figma imports
  // here.
  const { enabled: selfServeEnabled } = useSelfServeAccess();
  const showBuilderShortcut = selfServeEnabled && (isPlatformAdmin || canvasModesEnabled);

  // Both header buttons land on a doorway that mints a new template, so an
  // over-quota user gets the upgrade prompt rather than a page that can only
  // 403. Undefined while loading means "allow" — the server is the real gate.
  const { data: quota } = useBillingControllerGetQuota(orgId, undefined, {
    query: { enabled: !!orgId },
  });
  const quotaExceeded = !!quota && !quota.canGenerate;
  const [showUpgrade, setShowUpgrade] = useState(false);

  const goOrUpgrade = (to: string) => {
    if (quotaExceeded) {
      setShowUpgrade(true);
      return;
    }
    navigate({ to });
  };

  // Only read while the strip is rendered, which is only when both tabs exist.
  const activeTab = activeTabFromPath(location.pathname);
  const { relationship, isTrial, isLoading: wgLoading } = useWhiteGloveAccess();
  const isManaged = hasWhiteGloveWork(relationship) && !isTrial;
  // "Create email" is how a brand without the builder gets a new email: a paying
  // White Glove brand files a one-off ticket, a brand on its free designs is
  // shown their progress, and anyone else is sent to the free designs (which
  // shows the service instead when this brand's are spent). A self-serve brand
  // already has "Create with AI", so it only gets this when it is also managed.
  const showCreateEmail = activeTab === 'my' && !wgLoading && (isManaged || !selfServeEnabled);
  const [showOneOffComposer, setShowOneOffComposer] = useState(false);

  const handleCreateEmail = () => {
    if (isManaged) setShowOneOffComposer(true);
    else navigate({ to: isTrial ? tp.whiteGloveTasks : tp.whiteGloveTry });
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: 'library' | 'my') => {
    if (newValue === 'library') navigate({ to: `${base}/templates/library` });
    else navigate({ to: `${base}/templates/my` });
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 } }}>
      <Box
        sx={{
          mb: { xs: 2, sm: 4 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Typography
          variant="h3"
          fontWeight="bold"
          sx={{
            fontSize: { xs: 24, sm: 'inherit' },
            lineHeight: 1.2,
            flex: 1,
            minWidth: 0,
          }}
        >
          {/* "Template Library" is the name of a tab a managed brand does not
              have. With the strip gone the heading is the only thing naming the
              page, so it has to name what is actually on it. */}
          {selfServeEnabled ? t('page.title') : t('tabs.my')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
          {showCreateEmail && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateEmail}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {t('page.createEmail')}
            </Button>
          )}
          {/* "Open Email Builder" is a power-user shortcut; hidden on mobile
              to keep the header from wrapping. Gated behind the canvas flag
              (platform admins or canvas_creative_modes orgs). */}
          {showBuilderShortcut && (
            <Button
              variant="outlined"
              startIcon={<ArchitectureIcon />}
              onClick={() => goOrUpgrade(tp.emailNew)}
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            >
              {t('page.openBuilder')}
            </Button>
          )}
          {selfServeEnabled && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AutoAwesomeIcon />}
              onClick={() => goOrUpgrade(tp.emailCreate)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {t('page.createWithAI')}
            </Button>
          )}
        </Box>
      </Box>

      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        type="generation"
        organizationId={orgId}
        used={quota?.used ?? 0}
        limit={quota?.limit ?? 0}
        nextPlanName={quota?.nextPlanName}
      />

      <ComposerDialog
        open={showOneOffComposer}
        oneOff
        onClose={() => setShowOneOffComposer(false)}
      />

      {/* A brand without the self-serve builder has exactly one tab, and a
          one-tab tab strip is a control that cannot be operated: it reads as a
          filter the page is stuck in rather than as the only thing there is.
          The heading above already says what the page is. */}
      {selfServeEnabled && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label={t('tabs.library')} value="library" />
            <Tab label={t('tabs.my')} value="my" />
          </Tabs>
        </Box>
      )}

      <Outlet />
    </Container>
  );
}
